/**
 * Assessment — Finding Your Voice
 *
 * A three-part diagnostic conversation led by Philemon, Xenia, and Theron.
 * 12 questions mapping to scoring dimensions that drive module recommendations.
 */

// ---------------------------------------------------------------------------
// Transition from Scene Setter to Assessment
// ---------------------------------------------------------------------------

export const ASSESSMENT_INTRO = {
  id: 'assessmentIntro',
  lines: [
    { character: 'Xenia', text: 'So. Where to begin?' },
    { character: 'Theron', text: 'I say we just ask.' },
    {
      character: 'Philemon',
      text: 'Gently, Theron.',
    },
    {
      character: 'Theron',
      text: 'I\'m always gentle. I\'m like a breeze.',
    },
    {
      character: 'Xenia',
      text: 'You\'re like a gale. But he\'s right. Let\'s not overthink this.',
    },
    {
      character: null,
      text: 'Xenia turns to you.',
    },
    {
      character: 'Xenia',
      text: 'We\'d like to understand where you are right now: with your voice, your fears, and what you\'re hoping for. There are no wrong answers. Just honest ones.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Assessment Questions (12 total, three parts)
// ---------------------------------------------------------------------------

export const ASSESSMENT_QUESTIONS = [
  // =========================================================================
  // PART 1: Philemon's Questions — The Inner Landscape
  // =========================================================================
  {
    part: 1,
    partTitle: 'The Inner Landscape',
    partIntro:
      'Philemon leans forward slightly. He has the gentle voice of someone who\'s sat with his own fears.',
    questionNumber: 1,
    id: 'weight-of-it',
    title: 'The Weight of It',
    character: 'philemon',
    prompt:
      'When you think about speaking, really speaking, where people can see you think and feel and stumble, what rises in you?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'Dread. A physical heaviness. I\'d rather do almost anything else.',
        emoji: '😰',
        score: { comfortWithFear: 1 },
      },
      {
        id: 'b',
        text: 'Significant anxiety. I can push through, but it costs me.',
        emoji: '😟',
        score: { comfortWithFear: 3 },
      },
      {
        id: 'c',
        text: 'Mild nervousness. Uncomfortable but manageable.',
        emoji: '😐',
        score: { comfortWithFear: 6 },
      },
      {
        id: 'd',
        text: 'Not much, honestly. I\'m more curious than afraid.',
        emoji: '🙂',
        score: { comfortWithFear: 9 },
      },
    ],
  },
  {
    part: 1,
    questionNumber: 2,
    id: 'voice-inside',
    title: 'The Voice Inside',
    character: 'philemon',
    prompt:
      'We all have a voice that speaks to us before we speak to others. What does yours tend to say?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'They\'ll see you don\'t belong here. They\'ll know you\'re not enough.',
        emoji: '😨',
        score: { innerVoice: 1 },
      },
      {
        id: 'b',
        text: 'Don\'t mess this up. You\'ll look foolish. Play it safe.',
        emoji: '😬',
        score: { innerVoice: 3 },
      },
      {
        id: 'c',
        text: 'You\'d better be impressive. Anything less is failure.',
        emoji: '😤',
        score: { innerVoice: 4 },
      },
      {
        id: 'd',
        text: 'I wonder what will happen. Let\'s find out.',
        emoji: '🤔',
        score: { innerVoice: 8 },
      },
      {
        id: 'e',
        text: 'Honestly? It\'s pretty quiet in there.',
        emoji: '😌',
        score: { innerVoice: 9 },
      },
    ],
  },
  {
    part: 1,
    questionNumber: 3,
    id: 'the-history',
    title: 'The History',
    character: 'philemon',
    intro: 'Philemon pauses. His eyes are kind.',
    prompt:
      'Sometimes our fear has roots: a moment when speaking went badly, and something in us decided "never again." Does anything come to mind?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'Yes. I can picture a specific moment that still affects me.',
        emoji: '💔',
        score: { comfortWithFear: 1, innerVoice: 1 },
      },
      {
        id: 'b',
        text: 'There have been several difficult experiences over time.',
        emoji: '😞',
        score: { comfortWithFear: 3, innerVoice: 3 },
      },
      {
        id: 'c',
        text: 'Nothing dramatic, but a general pattern of discomfort.',
        emoji: '😕',
        score: { comfortWithFear: 5, innerVoice: 5 },
      },
      {
        id: 'd',
        text: 'Not really. My unease doesn\'t seem connected to any particular event.',
        emoji: '🤷',
        score: { comfortWithFear: 7, innerVoice: 7 },
      },
    ],
  },
  {
    part: 1,
    questionNumber: 4,
    id: 'the-hiding',
    // What you do when afraid nudges a result by a point; it is not a rating.
    scoring: 'adjust',
    title: 'The Hiding',
    character: 'philemon',
    prompt:
      'When the fear is strongest, what do you do? No judgement. I\'ve done all of these myself.',
    type: 'multi',
    options: [
      {
        id: 'a',
        text: 'Avoid the situation entirely if I can.',
        emoji: '🚪',
        score: { comfortWithFear: -1 },
      },
      {
        id: 'b',
        text: 'Over-prepare until I\'ve memorised every word.',
        emoji: '📝',
        score: { clarityOfMessage: 1 },
      },
      {
        id: 'c',
        text: 'Speak as quickly as possible to get it over with.',
        emoji: '⏩',
        score: { presenceDelivery: -1 },
      },
      {
        id: 'd',
        text: 'Deflect attention: make jokes, change the subject.',
        emoji: '🃏',
        score: { connectionReadingRoom: -1 },
      },
      {
        id: 'e',
        text: 'Go blank. My mind empties and I lose my thread.',
        emoji: '🫥',
        score: { presenceDelivery: -1 },
      },
      {
        id: 'f',
        text: 'Become very small: quiet voice, minimal presence.',
        emoji: '🤐',
        score: { presenceDelivery: -1 },
      },
      {
        id: 'g',
        text: 'Something else (I\'ve developed my own coping strategies).',
        emoji: '🔧',
        score: {},
      },
    ],
  },

  // =========================================================================
  // TRANSITION: Philemon → Xenia
  // =========================================================================
  // (Handled via PART_TRANSITIONS below)

  // =========================================================================
  // PART 2: Xenia's Questions — The Relational Landscape
  // =========================================================================
  {
    part: 2,
    partTitle: 'The Relational Landscape',
    partIntro:
      'Xenia shifts on her cushion. "Philemon asks about what happens inside you. I\'m curious about what happens between you and others."',
    questionNumber: 5,
    id: 'rooms-you-enter',
    // Which rooms matter is a topic, not a level: it does not move a result.
    scoring: 'none',
    title: 'The Rooms You Enter',
    character: 'xenia',
    prompt:
      'Think about the situations where you need to speak. Which of these feel most relevant to you right now?',
    type: 'multi',
    options: [
      {
        id: 'a',
        text: 'Small groups: meetings, dinners, conversations where I want to contribute but hold back.',
        emoji: '👥',
        score: { connectionReadingRoom: 2 },
      },
      {
        id: 'b',
        text: 'Formal presentations: standing up in front of a room with all eyes on me.',
        emoji: '🎤',
        score: { presenceDelivery: 2 },
      },
      {
        id: 'c',
        text: 'One-on-one conversations, especially difficult or emotionally charged ones.',
        emoji: '💬',
        score: { connectionReadingRoom: 3 },
      },
      {
        id: 'd',
        text: 'Social situations: parties, networking, anywhere I\'m expected to be "on."',
        emoji: '🎉',
        score: { connectionReadingRoom: 1 },
      },
      {
        id: 'e',
        text: 'Creative or personal expression: sharing something I\'ve made or something I believe.',
        emoji: '🎨',
        score: { clarityOfMessage: 2 },
      },
      {
        id: 'f',
        text: 'Authority situations: speaking to someone with power over me.',
        emoji: '👔',
        score: { comfortWithFear: 1 },
      },
    ],
  },
  {
    part: 2,
    questionNumber: 6,
    id: 'the-listening-question',
    title: 'The Listening Question',
    character: 'xenia',
    prompt:
      'When someone else is speaking and the room is really listening, leaning in, engaged, connected, what do you feel?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'Envy. I want that but can\'t imagine having it.',
        emoji: '😔',
        score: { connectionReadingRoom: 2, comfortWithFear: 1 },
      },
      {
        id: 'b',
        text: 'Curiosity. I study what they\'re doing, trying to understand the magic.',
        emoji: '🧐',
        score: { connectionReadingRoom: 5, presenceDelivery: 3 },
      },
      {
        id: 'c',
        text: 'Inspiration. It reminds me that real connection through speaking is possible.',
        emoji: '✨',
        score: { connectionReadingRoom: 7 },
      },
      {
        id: 'd',
        text: 'Recognition. I\'ve felt that too, in my better moments.',
        emoji: '🙂',
        score: { connectionReadingRoom: 8 },
      },
    ],
  },
  {
    part: 2,
    questionNumber: 7,
    id: 'connection-seeking',
    // What "well" looks like is a preference, not a level: it does not move a result.
    scoring: 'none',
    title: 'The Connection You\'re Seeking',
    character: 'xenia',
    prompt:
      'When you imagine yourself speaking well, not perfectly but well, what does "well" look like to you?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'People understand what I mean. My ideas land clearly.',
        emoji: '🎯',
        score: { clarityOfMessage: 3 },
      },
      {
        id: 'b',
        text: 'People feel something. There\'s an emotional resonance.',
        emoji: '💗',
        score: { connectionReadingRoom: 3 },
      },
      {
        id: 'c',
        text: 'I feel present and real. Not performing, just being myself.',
        emoji: '🌿',
        score: { presenceDelivery: 3 },
      },
      {
        id: 'd',
        text: 'People are moved to act. Something changes because I spoke.',
        emoji: '🔥',
        score: { clarityOfMessage: 2, presenceDelivery: 2 },
      },
    ],
  },
  {
    part: 2,
    questionNumber: 8,
    id: 'rooms-response',
    title: 'The Room\'s Response',
    character: 'xenia',
    prompt:
      'How well do you read a room? When you\'re speaking, can you sense what\'s happening in the people listening?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'I can\'t. I\'m too inside my own head to notice anyone else.',
        emoji: '🌀',
        score: { connectionReadingRoom: 1 },
      },
      {
        id: 'b',
        text: 'I notice strong signals (boredom, hostility, enthusiasm) but miss the subtleties.',
        emoji: '📡',
        score: { connectionReadingRoom: 4 },
      },
      {
        id: 'c',
        text: 'I\'m reasonably attuned. I can usually tell if people are with me or not.',
        emoji: '👁️',
        score: { connectionReadingRoom: 7 },
      },
      {
        id: 'd',
        text: 'I\'m very aware, sometimes too aware. I over-read every reaction.',
        emoji: '🔮',
        score: { connectionReadingRoom: 6, comfortWithFear: 3 },
      },
    ],
  },

  // =========================================================================
  // PART 3: Theron's Questions — The Practical Landscape
  // =========================================================================
  {
    part: 3,
    partTitle: 'The Practical Landscape',
    partIntro:
      'Theron sets down his cup. "Alright. Enough about feelings. Let\'s talk about reality."',
    questionNumber: 9,
    id: 'the-situation',
    title: 'The Situation',
    character: 'theron',
    prompt:
      'Is there a specific speaking situation coming up that\'s on your mind? Something real and concrete?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'Yes, something in the next few days that\'s keeping me up at night.',
        emoji: '🔴',
        score: { comfortWithFear: 1 },
      },
      {
        id: 'b',
        text: 'Yes, something in the coming weeks or months I need to prepare for.',
        emoji: '🟠',
        score: {}, // says when, not how well
      },
      {
        id: 'c',
        text: 'Nothing specific, but I regularly face situations where I need to speak better.',
        emoji: '🟡',
        score: {}, // says when, not how well
      },
      {
        id: 'd',
        text: 'No. This is more of a general desire to grow.',
        emoji: '🟢',
        score: { comfortWithFear: 7 },
      },
    ],
  },
  {
    part: 3,
    questionNumber: 10,
    id: 'the-stakes',
    // What is at stake is a topic, not a level: it does not move a result.
    scoring: 'none',
    title: 'The Stakes',
    character: 'theron',
    prompt:
      'What\'s actually at stake for you? What could change if you found your voice? Pick all that apply.',
    type: 'multi',
    options: [
      {
        id: 'a',
        text: 'Career: promotions, leadership, being taken seriously at work.',
        emoji: '💼',
        score: { clarityOfMessage: 2 },
      },
      {
        id: 'b',
        text: 'Relationships: saying what I really feel to the people who matter.',
        emoji: '❤️',
        score: { connectionReadingRoom: 2 },
      },
      {
        id: 'c',
        text: 'Self-respect: I\'m tired of being smaller than I know I am.',
        emoji: '🦁',
        score: { comfortWithFear: 2 },
      },
      {
        id: 'd',
        text: 'Impact: I have something to say that could help others.',
        emoji: '🌍',
        score: { presenceDelivery: 2 },
      },
      {
        id: 'e',
        text: 'Freedom: I want to stop letting fear make my decisions.',
        emoji: '🕊️',
        score: { comfortWithFear: 1, innerVoice: 1 },
      },
    ],
  },
  {
    part: 3,
    questionNumber: 11,
    id: 'honest-assessment',
    title: 'The Honest Assessment',
    character: 'theron',
    prompt:
      'If you had to rate yourself as a communicator right now, and I mean honestly, not modestly, where would you put yourself?',
    type: 'single',
    options: [
      {
        id: 'a',
        text: 'Struggling. Speaking is difficult for me in most situations.',
        emoji: '1️⃣',
        score: { presenceDelivery: 1, clarityOfMessage: 1 },
      },
      {
        id: 'b',
        text: 'Below average. I get by, but I know I\'m not reaching people.',
        emoji: '3️⃣',
        score: { presenceDelivery: 3, clarityOfMessage: 3 },
      },
      {
        id: 'c',
        text: 'Decent. I can hold my own in some situations but fall apart in others.',
        emoji: '5️⃣',
        score: { presenceDelivery: 5, clarityOfMessage: 5 },
      },
      {
        id: 'd',
        text: 'Good, but I know there\'s another level I haven\'t reached.',
        emoji: '7️⃣',
        score: { presenceDelivery: 7, clarityOfMessage: 7 },
      },
      {
        id: 'e',
        text: 'Strong. I\'m here to refine, not rebuild.',
        emoji: '9️⃣',
        score: { presenceDelivery: 9, clarityOfMessage: 9 },
      },
    ],
  },
  {
    part: 3,
    questionNumber: 12,
    id: 'the-real-question',
    title: 'The Real Question',
    character: 'theron',
    intro:
      'Theron leans back and crosses his arms. Not closed off, just considering you.',
    prompt:
      'Last one. If we could only help you with one thing, the one shift that would matter most, what would it be?',
    type: 'single',
    options: [
      // Q12 drives module recommendations only — it deliberately carries no
      // score entries, because a zero-value score would still inflate the
      // averaging denominator in calculateScores and drag the dimension down.
      {
        id: 'a',
        text: 'I want to understand my fear and stop letting it control me.',
        emoji: '🔓',
        recommendedModules: [1, 2],
      },
      {
        id: 'b',
        text: 'I want to find my message, to know what I actually want to say.',
        emoji: '🧭',
        recommendedModules: [3, 2],
      },
      {
        id: 'c',
        text: 'I want to feel comfortable speaking with and to other people.',
        emoji: '🤝',
        recommendedModules: [4, 5],
      },
      {
        id: 'd',
        text: 'I want practical techniques: how to actually do this better.',
        emoji: '🛠️',
        recommendedModules: [6, 7],
      },
      {
        id: 'e',
        text: 'I want confidence. The deep kind, not the performance kind.',
        emoji: '🌱',
        recommendedModules: [1, 8],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Part Transitions (dialogue between question groups)
// ---------------------------------------------------------------------------

export const PART_TRANSITIONS = {
  '1-to-2': {
    lines: [
      {
        character: 'philemon',
        text: 'Thank you. That takes courage, naming the thing.',
      },
      {
        character: null,
        text: 'He glances at Xenia.',
      },
      {
        character: 'philemon',
        text: 'Your turn, I think.',
      },
    ],
  },
  '2-to-3': {
    lines: [
      {
        character: 'xenia',
        text: 'You\'re painting a picture. This is helpful.',
      },
      {
        character: null,
        text: 'She looks over at Theron, who has been listening with unusual patience.',
      },
      {
        character: 'xenia',
        text: 'Theron? You\'ve been quiet long enough.',
      },
      {
        character: 'theron',
        text: 'I thought you\'d never finish.',
      },
      {
        character: 'xenia',
        text: 'Liar. You were interested.',
      },
      {
        character: 'theron',
        text: '...Perhaps.',
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Scoring Dimensions
// ---------------------------------------------------------------------------

export const SCORING_DIMENSIONS = [
  {
    id: 'comfortWithFear',
    label: 'Comfort with Fear',
    description:
      'How you relate to the fear and anxiety that speaking can bring. Lower scores suggest significant fear; higher scores suggest ease.',
    min: 0,
    max: 10,
  },
  {
    id: 'innerVoice',
    label: 'Inner Voice',
    description:
      'The quality of your internal dialogue before and during speaking. Lower scores suggest a harsh, critical inner voice; higher scores suggest a supportive or quiet one.',
    min: 0,
    max: 10,
  },
  {
    id: 'clarityOfMessage',
    label: 'Clarity of Message',
    description:
      'How clearly you know what you want to say and why it matters. Lower scores suggest confusion or lack of focus; higher scores suggest sharp clarity.',
    min: 0,
    max: 10,
  },
  {
    id: 'presenceDelivery',
    label: 'Presence & Delivery',
    description:
      'Your ability to be present in the moment and deliver your message with intention. Lower scores suggest significant struggle; higher scores suggest natural presence.',
    min: 0,
    max: 10,
  },
  {
    id: 'connectionReadingRoom',
    label: 'Connection & Reading the Room',
    description:
      'How well you connect with listeners and sense what\'s happening in the room. Lower scores suggest disconnection; higher scores suggest a strong sense of the room.',
    min: 0,
    max: 10,
  },
];

// ---------------------------------------------------------------------------
// Module Recommendation Map (driven primarily by Q12)
// ---------------------------------------------------------------------------

export const MODULE_RECOMMENDATION_MAP = {
  understandFear: {
    q12Option: 'a',
    label: 'Understanding and befriending your fear',
    primaryModule: 1,
    secondaryModule: 2,
  },
  findingMessage: {
    q12Option: 'b',
    label: 'Finding your message',
    primaryModule: 3,
    secondaryModule: 2,
  },
  comfortWithOthers: {
    q12Option: 'c',
    label: 'Comfort in connection',
    primaryModule: 4,
    secondaryModule: 5,
  },
  practicalTechniques: {
    q12Option: 'd',
    label: 'Practical speaking techniques',
    primaryModule: 6,
    secondaryModule: 7,
  },
  deepConfidence: {
    q12Option: 'e',
    label: 'Building deep confidence',
    primaryModule: 1,
    secondaryModule: 8,
  },
};

// ---------------------------------------------------------------------------
// Assessment Closing Dialogue
// ---------------------------------------------------------------------------

export const ASSESSMENT_CLOSING = {
  id: 'assessmentClosing',
  lines: [
    {
      character: 'theron',
      text: 'Well. That\'s a starting point.',
    },
    {
      character: 'philemon',
      text: 'More than a starting point. You\'ve told us a great deal, and you\'ve been honest doing it.',
    },
    {
      character: 'xenia',
      text: 'Now let us sit with what you\'ve shared. We\'ll come back with something: not a prescription, but a path. Your path.',
    },
    {
      character: 'theron',
      text: 'And for what it\'s worth, the fact that you\'re here, answering these questions? That\'s not nothing.',
    },
    {
      character: 'philemon',
      text: 'The voice you\'re looking for isn\'t somewhere else. It\'s already in you. We\'re just going to help you stop getting in its way.',
    },
    {
      character: 'xenia',
      text: 'Welcome to the courtyard.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Results Templates — character-driven observations for each dimension
// ---------------------------------------------------------------------------

export const RESULTS_TEMPLATES = [
  {
    dimension: 'comfortWithFear',
    character: 'philemon',
    low: {
      threshold: 3,
      observation:
        'From what you\'ve told us, your fear runs deep, and that\'s not a weakness. It means you care about being seen as you are. But right now, the fear is making your decisions for you. We need to change that relationship.',
      guidance:
        'We\'ll start by understanding your fear, not fighting it. The trembling can come. It just doesn\'t get to drive.',
    },
    mid: {
      threshold: 6,
      observation:
        'You feel the fear but you push through it. That takes real strength. The question is whether the pushing is costing you more than it needs to.',
      guidance:
        'Let\'s find ways to work with your nervous energy rather than against it. There\'s power in that tension if you know how to use it.',
    },
    high: {
      threshold: 10,
      observation:
        'You\'ve made your peace with the discomfort. That counts for a great deal. Your growth will come from depth, not from overcoming.',
      guidance:
        'We can focus less on managing fear and more on channelling the freedom you already have into something meaningful.',
    },
  },
  {
    dimension: 'innerVoice',
    character: 'philemon',
    low: {
      threshold: 3,
      observation:
        'That voice inside you, the one that says you\'re not enough, it\'s loud, isn\'t it? I know that voice well. It\'s been with me too. It lies, but it\'s very convincing.',
      guidance:
        'We won\'t try to silence it. We\'ll learn to hear it without obeying it. That\'s a different thing entirely.',
    },
    mid: {
      threshold: 6,
      observation:
        'The critic inside you is active but not paralysing. You can hear it, question it, sometimes override it. That is a skill already.',
      guidance:
        'Let\'s build on that awareness. We are not aiming for a quiet mind, only for one where the helpful voices speak louder than the frightened ones.',
    },
    high: {
      threshold: 10,
      observation:
        'You\'ve found a relatively peaceful inner landscape. The way you speak to yourself supports rather than sabotages you.',
      guidance:
        'This is a real asset. We can use this foundation to go deeper into the craft of speaking itself.',
    },
  },
  {
    dimension: 'clarityOfMessage',
    character: 'theron',
    low: {
      threshold: 3,
      observation:
        'You can\'t speak well if you don\'t know what you\'re saying. Not the words. The meaning underneath them. Right now, that\'s foggy for you.',
      guidance:
        'We\'ll work on finding your message. Not a script. A core. Something true that you can build everything else around.',
    },
    mid: {
      threshold: 6,
      observation:
        'You\'ve got something to say. You just haven\'t quite distilled it yet. The raw material is there. We need to shape it.',
      guidance:
        'Let\'s sharpen your message until it cuts. The clearer you are about what you mean, the less everything else matters.',
    },
    high: {
      threshold: 10,
      observation:
        'You know what you want to say. That\'s half the battle won before you open your mouth.',
      guidance:
        'Your growth is in delivery and connection now. The message is ready. Let\'s make sure it lands.',
    },
  },
  {
    dimension: 'presenceDelivery',
    character: 'theron',
    low: {
      threshold: 3,
      observation:
        'Right now, when you speak, something gets lost between what you mean and what comes out. The cargo arrives damaged. That\'s fixable.',
      guidance:
        'We\'ll work on the practical side: breath, pace, pausing, using your body. These aren\'t tricks. They\'re tools.',
    },
    mid: {
      threshold: 6,
      observation:
        'You can deliver when conditions are right. The challenge is consistency: being present even when the room is hard or the stakes are high.',
      guidance:
        'Let\'s build your range. You\'ve got firm ground to start from. Now we stretch it into the situations that currently throw you.',
    },
    high: {
      threshold: 10,
      observation:
        'By your own account, you\'ve got presence. If people feel it when you speak, that\'s not something everyone has. Protect it.',
      guidance:
        'Your edge is in subtlety now. Fine-tuning. Making the good moments more consistent and the great moments more frequent.',
    },
  },
  {
    dimension: 'connectionReadingRoom',
    character: 'xenia',
    low: {
      threshold: 3,
      observation:
        'When you speak, you disappear into yourself. The people listening become abstract: a blur, a threat. That distance is getting in your way right now.',
      guidance:
        'We\'ll practise staying with your listeners while you speak. It starts with one person, one pair of eyes. We build from there.',
    },
    mid: {
      threshold: 6,
      observation:
        'You can feel the room, at least partially. You pick up on the big signals. What you\'re missing are the quieter ones: the subtle shifts that tell you whether to push forward or pull back.',
      guidance:
        'Let\'s sharpen your ear for a room. Speaking is a conversation, even when you\'re the only one talking.',
    },
    high: {
      threshold: 10,
      observation:
        'By the sound of it, you\'re attuned to others. You sense shifts and moods. That is a gift, and good speaking is built on it.',
      guidance:
        'Your challenge is to trust what you feel and let it guide you in the moment, rather than second-guessing what you sense.',
    },
  },
];

// ---------------------------------------------------------------------------
// Scoring Logic
// ---------------------------------------------------------------------------

/**
 * Calculate dimension scores from a set of answers.
 *
 * @param {Object} answers - Keyed by question id. Value is a single option id
 *   (for single-select) or an array of option ids (for multi-select).
 * @returns {Object} Scores keyed by dimension id, normalised to 0–10.
 */
export function calculateScores(answers) {
  const rawScores = {};
  const counts = {};
  const adjustments = {};

  SCORING_DIMENSIONS.forEach((dim) => {
    rawScores[dim.id] = 0;
    counts[dim.id] = 0;
    adjustments[dim.id] = 0;
  });

  // Each result is the AVERAGE of the answers that rate a level on the 1–9
  // scale (how heavy the dread is, how harsh the inner voice is, how well you
  // read a room, how you rate yourself). Questions about topics, such as which
  // rooms matter to you or what is at stake, carry `scoring: 'none'` and are
  // left out: their small "relevance" values used to be averaged in with the
  // ratings, which dragged every result towards 2. Someone who rated
  // themselves "Strong" and ticked three rooms was told they were mid-range,
  // and "high" on Presence was all but unreachable.
  //
  // What you do when the fear is strongest (`scoring: 'adjust'`) moves a
  // result down or up by a point per answer, after the average.
  ASSESSMENT_QUESTIONS.forEach((question) => {
    if (question.scoring === 'none') return;

    const answer = answers[question.id];
    if (!answer) return;

    const selectedIds = Array.isArray(answer) ? answer : [answer];

    selectedIds.forEach((optionId) => {
      const option = question.options.find((o) => o.id === optionId);
      if (!option || !option.score) return;

      Object.entries(option.score).forEach(([dimension, value]) => {
        if (!(dimension in rawScores)) return;
        if (question.scoring === 'adjust') {
          adjustments[dimension] += value;
        } else {
          rawScores[dimension] += value;
          counts[dimension] += 1;
        }
      });
    });
  });

  // Normalise: average the ratings, apply the adjustments, clamp to 0–10.
  const normalisedScores = {};
  SCORING_DIMENSIONS.forEach((dim) => {
    const base = counts[dim.id] > 0 ? rawScores[dim.id] / counts[dim.id] : 5; // mid-point if no data
    normalisedScores[dim.id] = Math.max(0, Math.min(10, Math.round(base + adjustments[dim.id])));
  });

  return normalisedScores;
}

/**
 * Get the recommended module pathway based on Q12 answer and overall scores.
 *
 * @param {Object} answers - The full answers object.
 * @param {Object} scores - The calculated dimension scores.
 * @returns {Object} { primary: number, secondary: number, label: string }
 */
export function getRecommendedModules(answers, scores) {
  const q12Answer = answers['the-real-question'];

  // Find matching recommendation from Q12
  const match = Object.values(MODULE_RECOMMENDATION_MAP).find(
    (rec) => rec.q12Option === q12Answer
  );

  if (match) {
    return {
      primary: match.primaryModule,
      secondary: match.secondaryModule,
      label: match.label,
    };
  }

  // Fallback: recommend based on lowest scoring dimension
  const lowestDim = SCORING_DIMENSIONS.reduce((lowest, dim) =>
    (scores[dim.id] || 5) < (scores[lowest.id] || 5) ? dim : lowest
  );

  const fallbackMap = {
    comfortWithFear: { primary: 1, secondary: 2, label: 'Working with your fear' },
    innerVoice: { primary: 1, secondary: 8, label: 'Quietening your inner critic' },
    clarityOfMessage: { primary: 3, secondary: 2, label: 'Finding your message' },
    presenceDelivery: { primary: 6, secondary: 7, label: 'Building your presence' },
    connectionReadingRoom: { primary: 4, secondary: 5, label: 'Deepening connection' },
  };

  return fallbackMap[lowestDim.id] || { primary: 1, secondary: 2, label: 'Starting at the beginning' };
}

/**
 * Get the character-driven result observations for a set of scores.
 *
 * @param {Object} scores - Dimension scores keyed by dimension id.
 * @returns {Array} Array of { dimension, character, observation, guidance } objects.
 */
export function getResultObservations(scores) {
  return RESULTS_TEMPLATES.map((template) => {
    const score = scores[template.dimension] || 5;

    let tier;
    if (score <= template.low.threshold) {
      tier = template.low;
    } else if (score <= template.mid.threshold) {
      tier = template.mid;
    } else {
      tier = template.high;
    }

    return {
      dimension: template.dimension,
      character: template.character,
      score,
      observation: tier.observation,
      guidance: tier.guidance,
    };
  });
}
